<script lang="ts" setup>
  import { LazyAppSidebarMobile } from '#components';
  import { useBasicSEO } from '~/composables/basicSeo';
  import { useUiStore } from '~/store/ui';
  import { isRunningAsPWA } from '~/utils/pwa';

  declare global {
    interface Window {
      getUserInfo?: () => void;
    }
  }

  useBasicSEO();
  const { pwaInstallBannerEnabled } = useRuntimeConfig().public;

  const { isDesktop, activeBreakpoint, windowWidth } = useDeviceType();
  const route = useRoute();
  const overlay = useOverlay();
  const uiStore = useUiStore();

  const isMobile = computed(() => ['xxs', 'xs', 'sm'].includes(activeBreakpoint.value));

  const isMounted = ref(false);
  const isBannerEnabled = computed(() => {
    if (!pwaInstallBannerEnabled) return false;
    if (isRunningAsPWA()) return false;
    return !isDesktop.value && stores.user.isLoggedIn;
  });

  const { store: welcomeStore } = useWelcomeFeature();
  const welcomeFeatureAvailable = computed(() => !!welcomeStore.value?.isAvailable);

  const useDesktopSidebar = computed(() => {
    return isMounted.value && ['2xl', '3xl'].includes(activeBreakpoint.value);
  });
  const useMobileSidebar = computed(() => !useDesktopSidebar.value);

  const mobileSidebar = overlay.create(LazyAppSidebarMobile);

  watch(
    () => uiStore.sidebarIsOpen,
    async (newValue, oldValue) => {
      if (useDesktopSidebar.value || newValue === oldValue) return;

      if (newValue) {
        await mobileSidebar.open();
      } else {
        mobileSidebar.close();
      }
    },
  );

  watch(useMobileSidebar, async (newValue, oldValue) => {
    if (!newValue && oldValue) {
      await nextTick();
      uiStore.toggleSidebar(true);
    }
  });

  watch(
    isBannerEnabled,
    (val) => {
      stores.user.setIsPwaBannerEnabled(val);
    },
    { immediate: true },
  );

  const showRightBar = computed(() => {
    if (import.meta.server) return false;
    if (route?.meta?.hideRightBar) return false;
    return stores.user.isLoggedIn && windowWidth.value >= 1600;
  });

  const showMobileBottomNav = computed(() => stores.user.isLoggedIn && isMobile.value);

  onMounted(() => {
    isMounted.value = true;
    // by default, hidden on mobile / visible on desktop
    uiStore.toggleSidebar(useDesktopSidebar.value);

    if (window.location.host !== 'maxduel.com') {
      window.getUserInfo = async function () {
        const sessionKey = await stores.user.getIdToken();
        // eslint-disable-next-line no-console
        console.log(`userId: ${stores.user.getUserId()}`);
        // eslint-disable-next-line no-console
        console.log(`sessionKey: ${sessionKey}`);
      };
    }
  });
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-[100vh] bg-[#141D27] overflow-hidden">
    <AppPWAInstallBanner v-if="stores.user.isPwaInstallBannerVisible" />
    <AppHeader />

    <div class="flex justify-center w-full">
      <template v-if="stores.user.isLoggedIn">
        <AppSidebarDesktop v-if="useDesktopSidebar" />
        <AppSidebarMobile v-else />
        <LazyWelcomeNotificationPanel v-if="welcomeFeatureAvailable" />
      </template>

      <CoreLogoSpinner
        v-if="stores.user.isCurrentUserDataFetching"
        class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      />
      <CoreContentContainer
        v-else
        class="flex flex-col min-h-screen mt-[70px] md:mt-[86px] overflow-hidden"
        :class="{ 'mt-[calc(70px+var(--pwa-banner-height))]': stores.user.isPwaBannerEnabled }"
      >
        <main>
          <slot />
        </main>
        <AppFooter />
      </CoreContentContainer>

      <AppRightbar v-if="showRightBar" :key="activeBreakpoint.toString()" />

      <LazyAppMobileBottomNav v-if="showMobileBottomNav" :key="activeBreakpoint.toString()" />
    </div>
  </div>
</template>
